import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';

import { Button, Icon } from 'antd';
import NoDataEnergyFlow from './NoDataEnergyFlow';

import { getMonitoringBounds, getMonitoringGraphData } from '../../data/monitoring/selectors';

import { strings } from '../../localization';

import sunIcon from './icons/sunIcon.svg';
import batteryIcon from './icons/batteryIcon.svg';
import gridIcon from './icons/gridIcon.svg';
import heaterIcon from './icons/heaterIcon.svg';
import coolingIcon from './icons/coolingIcon.svg';
import lightIcon from './icons/lightIcon.svg';
import ovenIcon from './icons/ovenIcon.svg';
import carBatteryIcon from './icons/carBatteryIcon.svg';
import airConditioningIcon from './icons/airConditioningIcon.svg';
import alwaysOnIcon from './icons/alwaysOnIcon.svg';
import coffeeIcon from './icons/coffeeIcon.svg';
import computerIcon from './icons/computerIcon.svg';
import dishWasherIcon from './icons/dishWasherIcon.svg';
import dryerIcon from './icons/dryerIcon.svg';
import eCarIcon from './icons/eCarIcon.svg';
import electronicsIcon from './icons/electronicsIcon.svg';
import fridgeIcon from './icons/fridgeIcon.svg';
import generalIcon from './icons/generalIcon.svg';
import hairDryerIcon from './icons/hairDryerIcon.svg';
import ironIcon from './icons/ironIcon.svg';
import kettleIcon from './icons/kettleIcon.svg';
import kitchenMachineIcon from './icons/kitchenMachineIcon.svg';
import laptopIcon from './icons/laptopIcon.svg';
import microwaveIcon from './icons/microwaveIcon.svg';
import mixerIcon from './icons/mixerIcon.svg';
import poolIcon from './icons/poolIcon.svg';
import saunaIcon from './icons/saunaIcon.svg';
import toasterIcon from './icons/toasterIcon.svg';
import tvIcon from './icons/tvIcon.svg';
import vacuumCleanerIcon from './icons/vacuumCleanerIcon.svg';
import washingMachineIcon from './icons/washingMachineIcon.svg';
import whirlpoolIcon from './icons/whirlpoolIcon.svg';
import heatPumpIcon from './icons/heatPumpIcon.svg';
import motorIcon from './icons/motorIcon.svg';
import waterPumpIcon from './icons/waterPumpIcon.svg';
import compressorIcon from './icons/compressorIcon.svg';
import inductionIcon from './icons/inductionIcon.svg';
import irPanelIcon from './icons/irPanelIcon.svg';
import steamerIcon from './icons/steamerIcon.svg';
import { getOnlySubmetersIfExists } from '../../utils';

const productionColor = '#007A15';      // mid green
const gridColor = '#FF0000';            // red
const batteryColor = '#006611';         // dark green
const usageColor = '#FF0000';           // red
const soldColor = '#00BF20';            // was WA-Bright #00CC22
const batterLoadingColor = '#003308';   // very dark green
const idleColor = '#E5E5E5';
const elementColor = '#808080';         // mid grey
const animationBubbleColor = '#E5E5E5'

const usageProductionWidthWeb = 790;
const usageProductionWidthMobile = 490;
const batteryXWeb = 695;
const batteryXMobile = 395;

let styles = {
  rect: {rx: 8, fillOpacity: 0},
  production: {stroke: productionColor, x: 5, y: 5, strokeWidth: 5, width: usageProductionWidthWeb, height: 120, icon: sunIcon },
  grid: {stroke: gridColor, x: 5, y: 250, strokeWidth: 5, width: 100, height: 120, icon: gridIcon},
  battery: {stroke: batteryColor, x: batteryXWeb, y: 250, strokeWidth: 5, width: 100, height: 120, icon: batteryIcon},
  usage: {stroke: usageColor, x: 5, y: 500, strokeWidth: 5, width: usageProductionWidthWeb, height: 120, icon: generalIcon},
  usageOnly: {stroke: usageColor, x: 5, y: 250, strokeWidth: 5, width: usageProductionWidthWeb, height: 120, icon: generalIcon},
}

const stylesMobile = {
  rect: {rx: 8, fillOpacity: 0},
  production: {stroke: productionColor, x: 5, y: 5, strokeWidth: 5, width: usageProductionWidthMobile, height: 120, icon: sunIcon },
  grid: {stroke: gridColor, x: 5, y: 170, strokeWidth: 5, width: 100, height: 120, icon: gridIcon},
  battery: {stroke: batteryColor, x: batteryXMobile, y: 170, strokeWidth: 5, width: 100, height: 120, icon: batteryIcon},
  usage: {stroke: usageColor, x: 5, y: 340, strokeWidth: 5, width: usageProductionWidthMobile, height: 120, icon: generalIcon},
  usageOnly: {stroke: usageColor, x: 5, y: 20, strokeWidth: 5, width: usageProductionWidthMobile, height: 120, icon: generalIcon},
}

const deviceIcons = {
  cooling: coolingIcon,
  heater: heaterIcon,
  light: lightIcon,
  oven: ovenIcon,
  carbattery: carBatteryIcon,
  airconditioning: airConditioningIcon,
  alwayson: alwaysOnIcon,
  coffee: coffeeIcon,
  computer: computerIcon,
  dishwasher: dishWasherIcon,
  dryer: dryerIcon,
  electroniccar: eCarIcon,
  electronics: electronicsIcon,
  fridge: fridgeIcon,
  general: generalIcon,
  hairdryer: hairDryerIcon,
  iron: ironIcon,
  kettle: kettleIcon,
  kitchenmachine: kitchenMachineIcon,
  laptop: laptopIcon,
  microwave: microwaveIcon,
  mixer: mixerIcon,
  pool: poolIcon,
  sauna: saunaIcon,
  toaster: toasterIcon,
  tv: tvIcon,
  vacuumcleaner: vacuumCleanerIcon,
  washingmachine: washingMachineIcon,
  whirlpool: whirlpoolIcon,
  heatpump: heatPumpIcon,
  motor: motorIcon,
  waterpump: waterPumpIcon,
  compressor: compressorIcon,
  induction: inductionIcon,
  irpanel: irPanelIcon,
  steamer: steamerIcon
}

let maxWatt = 0;

const intTwoChars = (i) => (`0${i}`).slice(-2);

const getDateTimeFromTimeStamp = (timeStamp) => {
  if(timeStamp === null || timeStamp < 0) {
    return undefined;
  }

  const fullDate = new Date(timeStamp);

  // current date
  // adjust 0 before single digit date
  const date = intTwoChars(fullDate.getDate());

  // current month
  const month = intTwoChars(fullDate.getMonth() + 1);

  // current year
  const year = fullDate.getFullYear();

  // current hours
  const hours = intTwoChars(fullDate.getHours());

  // current minutes
  const minutes = intTwoChars(fullDate.getMinutes());

  // current seconds
  const seconds = intTwoChars(fullDate.getSeconds());

  return `${year}-${month}-${date} ${hours}:${minutes}:${seconds}`;
}

function sumArray(array) {
  let sum = 0;

  for (let i = 0; i < array.length; i += 1) {
    sum += array[i];
  }

  return sum;
}

function avgArray(array) {
  let sum = 0;

  for (let i = 0; i < array.length; i += 1) {
    sum += array[i];
  }

  return array.length > 0 ? sum / array.length : 0;
}

const generateDataset = (isMobile) => {
  const monitoring = useSelector((state) => state.monitoring);
  const auth = useSelector((state) => state.auth);
  const app = useSelector((state) => state.app);
  const meters = useSelector((state) => state.meters);

  const newState = {auth, app, monitoring, meters};

  const graphData = getMonitoringGraphData(newState);

  const usageData = graphData.datasets.find(data => data.label === "SUM");
  const latestUsageTs = Math.max(...usageData.data.map(o => o.x));
  const latestUsage = usageData.data.find(data => data.x === latestUsageTs);
  const latestUsageWatt = latestUsage ? latestUsage.y : 0;

  const production = graphData.productionAndBattery.filter(x => x.category === "Production").sort((a,b) => b.watt - a.watt);
  const solarData = graphData.datasets.find(data => data.label === "SOLAR");
  const latestSolarTs = Math.max(...solarData.data.map(o => o.x));
  const latestSolar = solarData.data.find(data => data.x === latestSolarTs);
  const latestSolarWatt = sumArray(production.map(pv => pv.watt));

  const batteries = graphData.productionAndBattery.filter(x => x.category === "Storage").sort((a,b) => b.watt - a.watt);
  const batteryData = graphData.datasets.find(data => data.label === "BATT");
  const latestBatteryTs = Math.max(...batteryData.data.map(o => o.x));
  const latestBattery = batteryData.data.find(data => data.x === latestBatteryTs);
  const latestBatteryWatt = sumArray(batteries.map(pv => pv.watt));
  const latestBatterySoc = avgArray(batteries.map(pv => pv.soc ? pv.soc : 0));

  const selfProduced = latestSolarWatt + latestBatteryWatt;

  const gridWatt = latestUsageWatt - selfProduced;

  const actualBatteryWatt = latestBatteryWatt < 0 ? latestBatteryWatt * -1 : latestBatteryWatt;

  maxWatt = Math.max(latestUsageWatt, latestSolarWatt, actualBatteryWatt);

  const latestTS = Math.max(latestUsageTs, latestSolarTs, latestBatteryTs);
  const dateTime = getDateTimeFromTimeStamp(latestTS);

  let maxDevices = isMobile ? 7 : 11;
  let maxDevicePerRow = (maxDevices + 1) / 2;

  if (window.innerWidth <= 1200 && maxDevicePerRow > 4) {
    maxDevicePerRow = 4;
    maxDevices = 7;
  }

  const sortedDevices = getOnlySubmetersIfExists(graphData.devices).sort((a,b) => b.watt - a.watt);

  const topDevices = sortedDevices.slice(0, maxDevices);
  const devices = topDevices.map(rec => ({name: rec.name, watt: rec.watt, category: rec.category}));

  const data = [
  ]
  if ((batteries && batteries.length > 0 && batteries[0]) || (production && production.length > 0 && production[0])){
    data.push(
        {title: 'usage', label: strings.dashboard.usage, x: 5, y: 500, watt: latestUsageWatt, solarWatt: latestSolarWatt, batteryWatt: latestBatteryWatt, usageOnly: false},
        {title: 'battery', label: strings.dashboard.battery, x: 695, y: 250, watt: latestBatteryWatt, solarWatt: latestSolarWatt, usageWatt: latestUsageWatt, gridWatt, soc: latestBatterySoc},
        {title: 'production', label: strings.dashboard.production, x: 5, y: 5, watt: latestSolarWatt, usageWatt: latestUsageWatt, batteryWatt: latestBatteryWatt},
        {title: 'grid', label: strings.dashboard.grid, x: 5, y: 250, watt: gridWatt, batteryWatt: latestBatteryWatt, solarWatt: latestSolarWatt, usageWatt: latestUsageWatt},
    )
  } else {
    data.push(
        {title: 'usage', label: strings.dashboard.usage, x: 5, y: 500, watt: latestUsageWatt, solarWatt: latestSolarWatt, batteryWatt: latestBatteryWatt, usageOnly: true},
    )
  }

  return {
    dateTime,
    data,
    devices,
    maxDevicePerRow,
    batteries,
    production,
    latestTS,
  };
}

const getContainerColor = (data) => {

  const style = styles[data.title];
  const baseColor = data.watt === 0 ? idleColor : style.stroke;

  if (data.title === "grid") {
    const isOverProducing = (data.batteryWatt + data.solarWatt) > data.usageWatt;

    return isOverProducing ? soldColor : baseColor;
  }

  if (data.title === "battery") {
    const batteryIsLoadingFromGrid = data.watt < 0 && data.solarWatt < (data.watt * -1 + data.usageWatt);
    const batteryIsLoadingFromProduction = data.watt < 0 && data.solarWatt > 0;
    const batteryIsSellingToGrid = data.watt > 0 && data.solarWatt + data.watt > data.usageWatt;

    if (batteryIsLoadingFromGrid) {
      return gridColor;
    }
    if(batteryIsLoadingFromProduction) {
      return batterLoadingColor;
    }

    return batteryIsSellingToGrid ? soldColor : baseColor;
  }

  if (data.title === "usage") {
    const isTotallyPoweredBySolar = data.watt <= data.solarWatt;
    const isTotallyPoweredByBattery = data.watt <= (data.solarWatt + data.batteryWatt) && data.batteryWatt > 0;

    if (isTotallyPoweredBySolar) {
      return productionColor;
    }

    return isTotallyPoweredByBattery ? batteryColor : baseColor;
  }

  if (data.title === "production") {
    const isOverProducing = data.watt > 0 && (data.batteryWatt + data.watt) > data.usageWatt;

    return isOverProducing ? soldColor : baseColor;
  }

  return baseColor;
}

const calculateConnectionWidth = (watt) => {
  let actualWatt = watt;

  if (actualWatt < 0){
    actualWatt *= -1;
  }

  const calculatedWidth = actualWatt / (maxWatt / 100);

  if (calculatedWidth > 40) {
    return 40;
  }

  return calculatedWidth < 10 ? 10 : calculatedWidth;
}

const roundWatt = (watt) => {
  if (maxWatt >= 10000) {

    return Math.round(watt / 10) / 100;
  }

  if (watt < 1){

    return Math.round(watt * 10) / 10;
  }

  return Math.round(watt);
}

const createWattText = (watt) => {

  const roundedWatt = roundWatt(watt);

  if (maxWatt >= 10000) {
    return `${roundedWatt} kW`;
  }

  if (watt < 1) {
    return `${roundedWatt} W`;
  }

  return `${roundedWatt} W`;
}

const showBottomLabel = (data) => {

  if (data.title === 'usage' || data.watt < 0) {
    return false;
  }

  switch (data.title) {
    case 'production': {
      return true;
    }
    case 'battery': {
      return data.solarWatt < data.usageWatt && data.watt > 0;
    }
    case 'grid': {
      const producingFromBattery = data.batteryWatt > 0;
      const underProducing = producingFromBattery ? (data.solarWatt + data.batteryWatt) < data.usageWatt : data.solarWatt < data.usageWatt;

      return underProducing && data.watt > 0;
    }
    default: return false;
  }
}

const showTopLabel = (data) => {
  switch (data.title) {
    case 'production': {
      return false;
    }
    case 'usage': {
      return true;
    }
    case 'battery': {
      const batteryIsLoadingFromProduction = data.watt < 0 && data.solarWatt > data.usageWatt;

      return batteryIsLoadingFromProduction;
    }
    case 'grid': {
      const isOverProducing = data.solarWatt > data.usageWatt;
      const isLoadingBattery = data.batteryWatt < 0;

      if (isOverProducing && isLoadingBattery) {
        const actualBatteryWatt = data.batteryWatt * -1;
        return data.solarWatt - data.usageWatt > actualBatteryWatt;
      }

      return isOverProducing && data.watt < 0;
    }
    default: return false;
  }
}

const showSideLabel = (data) => {
  switch (data.title) {
    case 'production': {
      return false;
    }
    case 'usage': {
      return false;
    }
    case 'battery': {
      const remainingSolarWatt = data.solarWatt - data.usageWatt;
      const batteryIsLoadingFromGrid = data.watt < 0 && remainingSolarWatt < (data.watt * -1);
      const batteryIsSellingToGrid = data.watt > 0 && data.solarWatt + data.watt > data.usageWatt;

      return batteryIsLoadingFromGrid || batteryIsSellingToGrid;
    }
    case 'grid': {
      const remainingSolarWatt = data.solarWatt - data.usageWatt;
      const batteryIsLoadingFromGrid = data.batteryWatt < 0 && remainingSolarWatt < (data.batteryWatt * -1);
      const batteryIsSellingToGrid = data.batteryWatt > 0 && data.solarWatt + data.batteryWatt > data.usageWatt;

      return batteryIsLoadingFromGrid || batteryIsSellingToGrid;
    }
    default: return false;
  }
}

const createSideLabel = (data, style, watt) => {
  const connectionHeight = calculateConnectionWidth(watt);
  const lineY = style.y + style.height / 2 - connectionHeight / 2;
  const rectX = data.title === "battery" ? style.x - style.width + 2 : style.x + style.width - 2;
  const textX = data.title === "battery" ? style.x - (style.width + 2 ) / 2 : style.x + style.width + (style.width + 2 ) / 2;

  return <>
    <rect x={rectX} width={style.width} y={lineY -19} height="27px" fill={getContainerColor(data, style)} rx="6"/>
    <text x={textX} y={lineY -4} fontSize="18px" fill="white" textAnchor='middle'>{createWattText(watt)}</text>
  </>
}

const getSideLabelWatt = (data) => {
  if (data.title === "battery") {

    const isLoadedBySolar = data.watt < 0 && data.solarWatt > data.usageWatt;
    const isSelfUsed = data.watt > 0 && data.solarWatt < data.usageWatt;

    if (isLoadedBySolar) {
      return (data.watt * -1) - (data.solarWatt - data.usageWatt);
    }

    if (isSelfUsed) {
      return data.watt - (data.usageWatt - data.solarWatt);
    }

    return data.watt > 0 ? data.watt : data.watt * -1;
  }

  if (data.title === "grid") {
    const isSoldBySolar = data.watt < 0 && data.solarWatt > data.usageWatt && (data.solarWatt - data.usageWatt) > (data.batteryWatt * -1);
    const isBought = data.watt > 0 && data.solarWatt < data.usageWatt && data.batteryWatt < (data.usageWatt - data.solarWatt);

    if (isSoldBySolar) {
      return (data.watt * -1) - (data.solarWatt - data.usageWatt);
    }

    if (isBought) {
      return (data.batteryWatt * -1);
    }

    return data.watt > 0 ? data.watt : data.watt * -1;
  }

  return 0;
}

const getDisplayedWatt = (data) => {
  let displayedWatt = data.watt;
  const isPositionSide = showSideLabel(data);
  const isPositionBottom = showBottomLabel(data);
  const sideLabelWatt = getSideLabelWatt(data);

  if (isPositionSide && sideLabelWatt > 0) {

    const overProducedWatt = data.solarWatt - data.usageWatt;

    if (data.solarWatt > 0 && data.solarWatt < data.usageWatt && data.watt > 0){
      displayedWatt = data.title === "grid" ? data.usageWatt - data.solarWatt : data.usageWatt - data.solarWatt;
    } else {
      displayedWatt = isPositionBottom ? data.usageWatt : overProducedWatt;
    }
  }

  if (displayedWatt < 0) {
    displayedWatt *= -1;
  }

  return displayedWatt;
}

const createLabel = (data) => {
  const title = (data.title === 'usage' && data.usageOnly === true) ? 'usageOnly' : data.title;
  const style = styles[title];

  const isPositionTop = showTopLabel(data);
  const isPositionBottom = showBottomLabel(data);
  const isPositionSide = showSideLabel(data);

  const lineY = isPositionTop ? style.y + 12 : style.y + style.height - 12;
  const line2Y = isPositionTop ? style.y + 5 : style.y + style.height - 5;
  const textY = isPositionTop ? style.y + 13 : style.y + style.height - 1;

  const sideLabelWatt = getSideLabelWatt(data);

  const label = <>
    <line x1={style.x} x2={style.x + style.width} y1={lineY} y2={lineY} stroke={getContainerColor(data, style)} strokeWidth="10" strokeLinecap="butt"/>
    <line x1={style.x + 3} x2={style.x + style.width - 3} y1={line2Y} y2={line2Y} stroke={getContainerColor(data, style)} strokeWidth="8" strokeLinecap="butt"/>
    <text x={style.x + style.width / 2} y={textY} fontSize="18px" fill="white" textAnchor='middle'>{createWattText(getDisplayedWatt(data))}</text>
  </>

  return <>
    {isPositionBottom || isPositionTop ? label : null}
    {isPositionSide ? createSideLabel(data, style, sideLabelWatt) : null}
  </>
}

const createContainer = (data) =>  {
  const title = (data.title === 'usage' && data.usageOnly === true) ? 'usageOnly' : data.title;
  return <>
    <rect style={{...styles.rect, ...styles[title], stroke: getContainerColor(data)}}
          x={styles[title].x}
          y={styles[title].y}
          rx={styles.rect.rx}
    />
    {createLabel(data)}
  </>;
}

const getIcon = (data, category) => {
  if (data.title !== "usage" || !category) {
    return styles[data.title].icon;
  }

  const categoryIdentifier = category.toLowerCase();

  if (!(categoryIdentifier in deviceIcons)) {
    return deviceIcons.general;
  }

  return deviceIcons[categoryIdentifier];
}

const createElementName = (name, x, y) => {
  const textParts = [];
  const startX = x;
  let startY = y;
  const fontSize = 14;
  const parts = name.trim().split(" ");

  if (name.length <= 12 || parts.length <= 1) {
    return <text x={startX} y={startY} fontSize={fontSize} fill="black" textAnchor='middle'>{name}</text>
  }

  if (parts.length > 1) {
    startY -= 5;
  }

  if (parts.length === 2) {
    parts.forEach(part => {
      textParts.push(
          <text x={startX} y={startY} fontSize={fontSize} fill="black" textAnchor='middle'>{part}</text>
      )

      startY += 12;
    });
  } else {
    let firstPart = "";

    for (let index = 0; index < parts.length - 1; index += 1) {
      const part = parts[index];

      firstPart += `${part} `
    }

    textParts.push(
        <text x={startX} y={startY} fontSize={fontSize} fill="black" textAnchor='middle'>{firstPart}</text>
    )

    const lastPart = parts[parts.length - 1];
    startY += 12;

    textParts.push(
        <text x={startX} y={startY} fontSize={fontSize} fill="black" textAnchor='middle'>{lastPart}</text>
    )
  }

  return textParts;
}

const createElement = (data, x, y, name, watt, category, soc) => {

  let elementHeightModifier = 29;
  let titleHeightModifier = elementHeightModifier + 18;
  const stateOfCharge = soc !== undefined ? soc : data.soc;

  if (data.watt >= 0 && data.title !== "usage" || (data.title === "battery" && data.solarWatt <= 0)) {
    elementHeightModifier = 44;
    titleHeightModifier = elementHeightModifier - 14;
  }

  let iconY = y - elementHeightModifier + 7;

  const batteryStateOfCharge = stateOfCharge !== undefined ? `${stateOfCharge}%` : undefined;
  if (batteryStateOfCharge !== undefined) {
    iconY -= 5;
  }

  let bubble;


  if (watt && watt !== 0) {

    const actualWatt = watt < 0 ? watt * -1 : watt;
    const roundedWatt = roundWatt(actualWatt);

    const bubbleWidth = 25 + (roundedWatt.toString().length * 4);

    bubble = <>
      <rect
        width={bubbleWidth}
        height="20"
        x={x + 5}
        y={y - elementHeightModifier - 10}
        fill={getContainerColor(data)}
        rx="10"
      />
      <text x={x + 5 + bubbleWidth / 2} y={y - elementHeightModifier + 4} fontSize="14px" fill="white" textAnchor='middle'>{roundedWatt}</text>
    </>
  }

  return <>
    <rect
      width="60"
      height="60"
      x={x - 27}
      y={y - elementHeightModifier}
      fill="black"
      rx="15"
      fillOpacity="0.1"
    />
    <rect
      width="60"
      height="60"
      x={x - 30}
      y={y - elementHeightModifier - 3}
      fill={elementColor}
      rx="15"
    />
    <image x={x - 20} y={iconY} width="40" height="40" href={getIcon(data, category)} />
    {createElementName(name, x, y + titleHeightModifier)}
    {batteryStateOfCharge !== undefined ?
      <text x={x} y={iconY + 45} fontSize="12px" fill="white" textAnchor='middle'>{batteryStateOfCharge}</text>
      : null
    }
    {bubble}
  </>
}

const createElements = (data, dataSet) => {
  const {devices, maxDevicePerRow, batteries, production} = dataSet;
  const title = (data.title === 'usage' && data.usageOnly === true) ? 'usageOnly' : data.title;
  const style = styles[title];
  const elementWidth = 60;
  let elementSource;

  let x = style.x + style.width / 2;
  let y = style.y + style.height / 2;

  switch (data.title) {
    case 'production': {
      elementSource = production;
      break;
    }
    case 'usage': {
      elementSource = devices;
      break;
    }
    case 'battery': {
      elementSource = batteries;
      break;
    }
    default: elementSource = undefined;
  }

  if (elementSource === undefined || elementSource.length <= 0) {
    return createElement(data, x, y, data.label);
  }

  const hasMoreRows = elementSource.length >= maxDevicePerRow;
  const devicesInRow = hasMoreRows ? maxDevicePerRow : elementSource.length;

  x -= (devicesInRow * elementWidth) + elementWidth;
  let deviceWatt = 0;

  elementSource.map(item => {
    deviceWatt += item.watt;
  })

  const allUsageKnown = deviceWatt >= data.watt;

  if (!allUsageKnown && data.title === 'usage') {

    elementSource.push({name: strings.dashboard.otherDevices, watt: data.watt - deviceWatt, category: "general"})

    if (!hasMoreRows) {
      x -= elementWidth;
    }
  }

  let itemsInRow = 0;

  if (elementSource.length > maxDevicePerRow) {
    y = style.y + style.height / 4;
  }

  const elements = elementSource.map(element => {
    if(itemsInRow >= maxDevicePerRow) {
      itemsInRow = 0;
      x = style.x + (style.width / 2) - (elementSource.length - maxDevicePerRow) * elementWidth - elementWidth;
      y = style.y + (style.height - style.height / 4);
    }

    x += elementWidth * 2;
    itemsInRow += 1;
    return createElement(data, x, y, element.name, element.watt, element.category)
  });

  return elements;
}

const createCircles = (startX, targetX, startY, targetY, count, reversed, isHorizontal) => {

  if (isHorizontal) {
    const firstX = startX - 8;
    const circles = [{startX: firstX, targetX: firstX - 32, startY, targetY: startY , index: 1}];
    const minX = targetX;
    let modifier = -32;

    for(let i = 1; i <= count; i+=1){

      const newTargetX = Math.max(firstX + modifier - 32, minX);

      circles.push({startX: firstX + modifier, targetX: newTargetX, startY, targetY: startY, index: i+1})
      modifier -= 32;
    }

    return circles;
  }

  const firstY = reversed ? startY -8 : startY + 8;
  const circles = [{startX, targetX: startX, startY: firstY, targetY: firstY + 32, index: 1}];
  const maxY = reversed ? startY : targetY;
  let modifier = reversed ? -32 : 32;

  for(let i = 1; i <= count - 1; i+=1){

    const newTargetY = Math.min(firstY + modifier + 32, maxY);

    circles.push({startX, targetX: startX, startY: firstY + modifier, targetY: newTargetY, index: i+1})
    modifier = reversed ? modifier - 32 : modifier + 32;
  }

  return circles;
}

const renderPositionButtons = (position, changePosition, isMobile) => {

  if (isMobile === true) {
    return undefined;
  }

  return <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <Button
        type={position === 'left' ? 'primary' : 'default'}
        style={{marginTop: 10, marginBottom: 10, marginLeft: 5, marginRight: 5}}
        onClick={changePosition('left')}
    >
      <Icon type="left"/>
    </Button>
    <Button
        type={position === 'up' ? 'primary' : 'default'}
        style={{marginTop: 10, marginBottom: 10, marginLeft: 5, marginRight: 5}}
        onClick={changePosition('up')}
    >
      <Icon type="up"/>
    </Button>
    <Button
        type={position === 'down' ? 'primary' : 'default'}
        style={{marginTop: 10, marginBottom: 10, marginLeft: 5, marginRight: 5}}
        onClick={changePosition('down')}
    >
      <Icon type="down"/>
    </Button>
    <Button
        type={position === 'right' ? 'primary' : 'default'}
        style={{marginTop: 10, marginBottom: 10, marginLeft: 5, marginRight: 5}}
        onClick={changePosition('right')}
    >
      <Icon type="right"/>
    </Button>
  </div>;
}

function useDimensions() {
  const [size, setSize] = useState("");
  useLayoutEffect(() => {
    function updateSize() {
      setSize(window.innerWidth);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const getViewBoxDimensions = (isMobile, devices, maxDevicePerRow, usageOnly) => {
  const windowWidth = useDimensions();
  const x = 0;
  const y = 0;
  const mobileViewBoxWidth = 500;
  let webViewBoxWidth = 800;
  let mobileViewBoxHeight = usageOnly ? 170 : 500;
  let webViewBoxHeight = 625;

  if (devices.length >= maxDevicePerRow) {
    mobileViewBoxHeight += 120;
    webViewBoxHeight += 120;
  }

  if (windowWidth <= 1200) {
    webViewBoxWidth -= 120;
  } else if (windowWidth <= 1400) {
    webViewBoxWidth -= 60;
  }

  return isMobile ? `${x} ${y} ${mobileViewBoxWidth} ${mobileViewBoxHeight}` : `${x} ${y} ${webViewBoxWidth} ${webViewBoxHeight}`;
}

const setStyles = (isMobile, datSet) => {
  const usageProductionWidth = isMobile ? usageProductionWidthMobile : usageProductionWidthWeb;
  const batteryX = isMobile ? batteryXMobile : batteryXWeb;
  const windowWidth = useDimensions();
  const { devices, maxDevicePerRow, data } = datSet;

  const usageWatt = data.filter(x => x.title === "usage")[0].watt;

  let deviceWatt = 0;

  devices.map(item => {
    deviceWatt += item.watt;
  })

  const allUsageKnown = deviceWatt >= usageWatt;
  const usageElementCount = allUsageKnown ? devices.length : devices.length + 1;

  if (usageElementCount > maxDevicePerRow) {
    styles.usage.height = 240;
    stylesMobile.usage.height = 240;
    styles.usageOnly.height = 240;
    stylesMobile.usageOnly.height = 240;
  } else {
    styles.usage.height = 120;
    stylesMobile.usage.height = 120;
    styles.usageOnly.height = 120;
    stylesMobile.usageOnly.height = 120;
  }

  if (isMobile) {
    styles = stylesMobile;
    return;
  }

  if (windowWidth <= 1200) {
    styles.usage.width = usageProductionWidth - 240;
    styles.usageOnly.width = usageProductionWidth - 240;
    styles.production.width = usageProductionWidth - 240;
    styles.battery.x = batteryX - 240;
  } else if (windowWidth <= 1400) {
    styles.usage.width = usageProductionWidth - 60;
    styles.usageOnly.width = usageProductionWidth - 60;
    styles.production.width = usageProductionWidth - 60;
    styles.battery.x = batteryX - 60;
  } else {
    styles.usage.width = usageProductionWidth;
    styles.usageOnly.width = usageProductionWidth;
    styles.production.width = usageProductionWidth;
    styles.battery.x = batteryX;
  }
}

function Chart(props) {
  const {
    graphBounds,
    position,
    changePosition,
    isMobile
  } = props;
  const dataSet = generateDataset(isMobile);
  const { usageOnly } = dataSet.data.find(data => data.title === 'usage') || false
  const timeLimitHideGraph = Math.max((graphBounds.from - graphBounds.to) / 400, 60000);

  const viewBoxDimensions = getViewBoxDimensions(isMobile, dataSet.devices, dataSet.maxDevicePerRow, usageOnly);
  setStyles(isMobile, dataSet);

  return (
      <div style={{ maxWidth: 800, maxHeight: 800, width:"inherit", display: "flex", flexDirection: "column"}}>
          {graphBounds.ts - graphBounds.to - dataSet.latestTS < timeLimitHideGraph
              ? <>
                  <div style={{ display: "flex", justifyContent: "space-between", flexDirection: "row-reverse", alignItems: "center" }}>
                      {renderPositionButtons(position, changePosition, isMobile)}
                      <div style={{ display: "flex", justifyContent: "center", fontSize: 18 }}>
                          {dataSet.dateTime}
                      </div>
                  </div>
                  <svg viewBox={viewBoxDimensions}>
                      {dataSet.data.map(data => (
                          <>
                              {createContainer(data)}
                              {createElements(data, dataSet)}
                              <Connection data={data} />
                          </>
                      ))}
                  </svg>
              </>
              : <>
                {renderPositionButtons(position, changePosition, isMobile)}
                <NoDataEnergyFlow />
              </>
          }
      </div>
  )
}

const createBatteryGridConnection = (data, style, styles, connections, width) => {
  const batterGridStrokeColor = data.watt < 0 ? gridColor : soldColor;
  const batteryGridStartX = style.x - 2;
  const batteryGridTargetX = styles.grid.x + styles.grid.width + 2;
  const batteryGridStartY = style.y + style.height / 2;
  const batteryGridConnection = <line x1={batteryGridStartX} x2={batteryGridTargetX} y1={batteryGridStartY} y2={batteryGridStartY} stroke={batterGridStrokeColor} strokeWidth={width} strokeLinecap="butt"/>;

  const batteryGridCircleCount = ((batteryGridStartX) - (styles.grid.width + 2)) / 32;
  const flowReversed = data.watt > 0;

  const animationStartX = flowReversed ? style.x - 2 : style.x - 32;

  if((data.solarWatt <= 0 && data.watt <= data.gridWatt) ||
      (data.watt > 0 && data.solarWatt >= data.usageWatt) ||
      (data.watt < 0 && data.solarWatt < data.usageWatt))
  {
    connections = [
      batteryGridConnection,
      <AnimatedCircle
          startX={animationStartX}
          targetX={batteryGridTargetX}
          startY={batteryGridStartY}
          targetY={batteryGridStartY}
          radius={width / 5}
          circleCount={batteryGridCircleCount}
          isHorizontal
          flowReversed={flowReversed}
      />
    ];
  } else{
    connections.push(batteryGridConnection);
    connections.push(
      <AnimatedCircle
        startX={animationStartX}
        targetX={batteryGridTargetX}
        startY={batteryGridStartY}
        targetY={batteryGridStartY}
        radius={width / 5}
        circleCount={batteryGridCircleCount}
        isHorizontal
        flowReversed={flowReversed}
        />
    );
  }

  return connections;
}

const Connection = ({data}) => {

  if (data.title === 'usage' || data.watt === 0 ||
      (data.title === 'grid' && data.watt > 0 && data.solarWatt >= data.usageWatt) ||
      (data.title === 'grid' && data.solarWatt <= 0 && data.batteryWatt > data.usageWatt) ||
      (data.title === 'grid' && data.watt < 0 && data.solarWatt < data.usageWatt) ||
      (data.title === 'grid' && data.batteryWatt < 0 && data.solarWatt > data.usageWatt && data.solarWatt + data.usageWatt < data.batteryWatt * -1))
  {
    return null;
  }

  const style = styles[data.title];

  const startX = style.x + style.width / 2;
  let startY = style.y + style.height + 2;
  let targetY = styles.usage.y - 2;
  let strokeColor = style.stroke;
  let actualWatt = data.watt;

  if (data.watt < 0) {
    startY = style.y - 2;
    targetY = styles.production.y + styles.production.height + 2;
    strokeColor = data.title === "battery" ? batterLoadingColor : soldColor;
    actualWatt *= -1;
  }

  const connectionHeight = targetY > startY ? targetY - startY : startY - targetY;
  const circleCount = connectionHeight / 25;

  const widthCalculationBase = getDisplayedWatt(data);

  const gridWatt = data.gridWatt < 0 ? data.gridWatt * -1 : data.gridWatt;
  const batteryConnectionWidthBase = Math.min(actualWatt, gridWatt);

  const width = calculateConnectionWidth(widthCalculationBase);
  const batteryConnectionWidth = calculateConnectionWidth(batteryConnectionWidthBase);
  const reversed = targetY < startY;

  let connections = [
    <line x1={startX} x2={startX} y1={startY} y2={targetY} stroke={strokeColor} strokeWidth={width} strokeLinecap="butt"/>,
    <AnimatedCircle startX={startX}
                    targetX={startX}
                    startY={startY}
                    targetY={targetY}
                    radius={width / 5}
                    circleCount={circleCount}
                    reversed={reversed}
    />
  ];

  if (data.title === "battery") {
    if (showSideLabel(data)) {
      connections = createBatteryGridConnection(data, style, styles, connections, batteryConnectionWidth);
    }
  }

  return connections;
}

function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const getOpacity = (isHorizontal, currentX, currentY, minX, maxY, reversed) => {
  if(isHorizontal === true) {
    return currentX < minX + 5 ? 0 : 1;
  }

  if (reversed) {
    return currentY > maxY - 5 ? 0 : 1;
  }

  return currentY > maxY + 5 ? 0 : 1;
}

const AnimatedCircle = ({ startX, targetX, startY, targetY, radius, circleCount, reversed, isHorizontal, flowReversed }) => {

  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);

  useInterval(() => {
    if (isHorizontal !== true) {
      if (positionY >= 32) {
        setPositionY(0);
      } else if (startY !== targetY) {
        setPositionY(positionY + 1);
      }

      if (positionX !== 0) {
        setPositionX(0);
      }

    } else if(isHorizontal === true) {
      if (flowReversed && positionX <= -32 ||
          !flowReversed && positionX >= 32) {
        setPositionX(0);
      } else if (startX !== targetX) {
        flowReversed ? setPositionX(positionX - 1) : setPositionX(positionX + 1);
      }

      if (positionY !== 0) {
        setPositionY(0);
      }
    }
  }, 50);

  const circles = createCircles(startX, targetX, startY, targetY, circleCount, reversed, isHorizontal);
  const maxY = reversed ? startY : targetY;

  return (
    circles.map(c =>
      <circle
        r={radius}
        fill={animationBubbleColor}
        cx={c.startX + positionX}
        cy={c.startY + positionY}
        opacity={getOpacity(isHorizontal === undefined ? false : isHorizontal, c.startX + positionX, c.startY + positionY, targetX, maxY, reversed)}
      />)
  )
}

export default connect(
    (state, ownProps) => ({
          ...ownProps,
          graphBounds: getMonitoringBounds(state),
    }), {}
)(Chart);
